import React, { useState, useMemo } from 'react';

interface InlineCalendarProps {
  value?: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  className?: string;
}

export const InlineCalendar: React.FC<InlineCalendarProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const date = new Date(value + 'T00:00:00');
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // 获取当月的所有日期
  const monthDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // 获取当月第一天是星期几 (0=周日, 1=周一, ..., 6=周六)
    const firstDay = new Date(year, month, 1).getDay();
    // 获取当月天数
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // 获取上个月天数
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days: Array<{
      date: number;
      isCurrentMonth: boolean;
      dateString: string;
      isToday: boolean;
    }> = [];

    // 添加上个月的尾部日期
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = daysInPrevMonth - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({
        date,
        isCurrentMonth: false,
        dateString: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`,
        isToday: false
      });
    }

    // 添加当月日期
    for (let date = 1; date <= daysInMonth; date++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
      const isToday = dateString === today.toISOString().split('T')[0];
      days.push({
        date,
        isCurrentMonth: true,
        dateString,
        isToday
      });
    }

    // 添加下个月的开头日期，补满6周
    const remainingDays = 42 - days.length; // 6周 × 7天
    for (let date = 1; date <= remainingDays; date++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      days.push({
        date,
        isCurrentMonth: false,
        dateString: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`,
        isToday: false
      });
    }

    return days;
  }, [currentMonth, today]);

  const formatMonthYear = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return `${year}年${month}月`;
  };

  const goToPrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleDateClick = (dateString: string) => {
    onChange(dateString);
  };

  const clearDate = () => {
    onChange('');
  };

  return (
    <div className={`inline-calendar bg-white border border-gray-200 rounded shadow-sm p-2 ${className}`}>
      {/* 头部导航 */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={goToPrevMonth}
          className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h3 className="text-xs font-medium text-gray-900">
          {formatMonthYear(currentMonth)}
        </h3>

        <button
          type="button"
          onClick={goToNextMonth}
          className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 星期标题 */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['日', '一', '二', '三', '四', '五', '六'].map(day => (
          <div key={day} className="text-center text-xs text-gray-500 py-0.5 h-5 flex items-center justify-center">
            {day}
          </div>
        ))}
      </div>

      {/* 日期网格 */}
      <div className="grid grid-cols-7 gap-0.5">
        {monthDays.map((day, index) => {
          const isSelected = value === day.dateString;

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDateClick(day.dateString)}
              className={`
                w-5 h-5 text-xs flex items-center justify-center rounded transition-colors
                ${day.isCurrentMonth
                  ? 'text-gray-900 hover:bg-blue-50'
                  : 'text-gray-300 hover:bg-gray-50'
                }
                ${isSelected
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : ''
                }
                ${day.isToday && !isSelected
                  ? 'bg-blue-100 text-blue-600 font-medium'
                  : ''
                }
              `}
            >
              {day.date}
            </button>
          );
        })}
      </div>

      {/* 清除按钮 */}
      {value && (
        <div className="mt-2 pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={clearDate}
            className="w-full text-xs text-gray-500 hover:text-gray-700 py-0.5"
          >
            清除日期
          </button>
        </div>
      )}
    </div>
  );
};